import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { AvatarGroup, Stack, Tooltip, Typography } from '@mui/material';

import { AvatarFacePile_InterviewersFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import AvatarWithRsvp from './AvatarWithRsvp';

// Note: these strings are used in tooltips and will be translated via the t() function inside the component
const responseStatusKeyMap = {
  [ResponseStatus.Accepted]: 'hiring-dashboard-common:common.rsvp.accepted',
  [ResponseStatus.Declined]: 'hiring-dashboard-common:common.rsvp.declinedLower',
  [ResponseStatus.Tentative]: 'hiring-dashboard-common:common.rsvp.maybeLower',
  [ResponseStatus.NeedsAction]: 'hiring-dashboard-common:common.rsvp.awaitingLower',
};

const responseStatusOrder = {
  [ResponseStatus.Declined]: 0,
  [ResponseStatus.Tentative]: 1,
  [ResponseStatus.NeedsAction]: 2,
  [ResponseStatus.Accepted]: 3,
};

interface Props {
  max?: number;
  tooltip?: string;
  dataTestId?: string;
  size?: 'sm' | 'md';
}

type Fragments = {
  interviewers: AvatarFacePile_InterviewersFragment[];
};

const AvatarFacePile: FCWithFragments<Fragments, Props> = ({
  dataTestId,
  interviewers,
  max = 3,
  tooltip,
  size = 'md',
}): JSX.Element | null => {
  const { t } = useTranslation();
  const sortedInterviewers = useMemo(() => {
    const interviewersCopy = [...interviewers];

    interviewersCopy.sort((a, b) => {
      const aStatus = a.responseStatus ?? ResponseStatus.NeedsAction;
      const bStatus = b.responseStatus ?? ResponseStatus.NeedsAction;

      if (responseStatusOrder[aStatus] > responseStatusOrder[bStatus]) {
        return 1;
      }

      if (responseStatusOrder[aStatus] < responseStatusOrder[bStatus]) {
        return -1;
      }

      return 0;
    });

    return interviewersCopy ?? [];
  }, [interviewers]);

  if (!sortedInterviewers?.length) {
    return null;
  }

  const tooltipDataList: string[] = sortedInterviewers.map((interviewerData) => {
    return t('hiring-dashboard-common:common.rsvp.nameWithStatus', {
      name: interviewerData.interviewer?.fullName ?? '',
      optionalTag: interviewerData.isOptional ? t('hiring-dashboard-common:common.optional') : '',
      status: t(responseStatusKeyMap[interviewerData.responseStatus ?? ResponseStatus.NeedsAction]),
    });
  });

  return (
    <Stack alignItems="flex-start">
      <Tooltip
        data-testid={dataTestId}
        title={
          tooltip ?? (
            <Stack>
              {tooltipDataList.map((tooltipText) => (
                <Typography key={tooltipText}>{tooltipText}</Typography>
              ))}
            </Stack>
          )
        }
      >
        <AvatarGroup
          max={max}
          sx={{
            '&>div': {
              width: size === 'sm' ? '20px' : '24px',
              height: size === 'sm' ? '20px' : '24px',
              borderRadius: '6px',
              fontSize: '12px',
            },
          }}
        >
          {sortedInterviewers.map((interviewer, index) => (
            <AvatarWithRsvp
              size={20}
              // eslint-disable-next-line react/no-array-index-key
              key={`${interviewer?.interviewerId}-${interviewer?.responseStatus}-${index}`}
              responseStatus={interviewer?.responseStatus}
              src={
                interviewer?.interviewer?.internalProfilePictureUrl || interviewer?.interviewer?.fullName || undefined
              }
              alt={interviewer?.interviewer?.fullName ?? ''}
              outlineOffset="-3px"
            />
          ))}
        </AvatarGroup>
      </Tooltip>
    </Stack>
  );
};

AvatarFacePile.fragments = {
  interviewers: gql`
    fragment AvatarFacePile_interviewers on ApplicationStageInterviewInterviewer {
      interviewerId
      responseStatus
      isOptional
      interviewer {
        id
        fullName
        internalProfilePictureUrl
      }
    }
  `,
};

export default AvatarFacePile;
